import React, { useEffect, useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import ThriveModal from "../../common/ThriveModal";
import { axiosInstance } from "../../../utils/networking";
import { ReminderSourceType } from "../../../interfaces";

export interface ReminderEditInitial {
  _id?: string;
  title?: string;
  note?: string;
  dueAt?: Date | string;
  case_id?: string | null;
  firm_name?: string | null;
  source?: ReminderSourceType;
}

interface ReminderEditModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  initial?: ReminderEditInitial;
}

const ReminderEditModal: React.FC<ReminderEditModalProps> = ({
  open,
  onClose,
  onSaved,
  initial,
}) => {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [dueAt, setDueAt] = useState<Dayjs | null>(dayjs().add(1, "day"));
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(initial?._id);

  useEffect(() => {
    if (open) {
      setTitle(initial?.title || "");
      setNote(initial?.note || "");
      setDueAt(initial?.dueAt ? dayjs(initial.dueAt) : dayjs().add(1, "day"));
    }
  }, [open, initial]);

  const handleSave = async () => {
    if (!title.trim() || !dueAt || !dueAt.isValid()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await axiosInstance.put(`/reminder/${initial!._id}`, {
          title: title.trim(),
          note,
          dueAt: dueAt.toISOString(),
        });
      } else {
        await axiosInstance.post(`/reminder`, {
          title: title.trim(),
          note,
          dueAt: dueAt.toISOString(),
          case_id: initial?.case_id || undefined,
          firm_name: initial?.firm_name || undefined,
          source: initial?.source || { kind: "manual" },
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      console.error("Failed to save reminder:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ThriveModal
      open={open}
      setOpen={(v: boolean) => !v && onClose()}
      closeByClick={true}
      width={520}
      height={"auto"}
      contentComponent={
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {isEdit ? "Edit Reminder" : "New Reminder"}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              autoFocus
              required
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                label="Due"
                value={dueAt}
                onChange={(v) => setDueAt(v)}
              />
            </LocalizationProvider>
            <TextField
              label="Note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            {initial?.firm_name && (
              <Typography variant="body2" sx={{ color: "#888" }}>
                Client: {initial.firm_name}
              </Typography>
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !title.trim()}
              >
                {isEdit ? "Save" : "Add Reminder"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      }
    />
  );
};

export default ReminderEditModal;
